import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  useTheme,
  FormControlLabel,
  Checkbox,
  styled,
} from "@mui/material";
import LocalStorageUtil from "../utils/localStorageUtil";
import { storageKeys } from "../utils/constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiService from "../apiService/ApiService";
import {
  AxiosResponse,
  MaterialCheck,
  MaterialUpdate,
  MaterialWiseTaskListingDropDown,
  PhotoUpdateForSameProjectTaskMaterial,
  TaskDetailsApi,
} from "../utils/urlConst";
import CameraView, {
  ImageArrayType,
  ImageType,
} from "../components/TaskDetailEdit/CameraView";
import TaskEditHeader from "../components/TaskDetailEdit/TaskEditHeader";
import TaskEditMaterialContainer from "../components/TaskDetailEdit/TaskEditMaterialContainer";
import TaskEditBottomContainer from "../components/TaskDetailEdit/TaskEditBottomContainer";
import TaskDetailsDropdownModal from "../components/TaskDetailEdit/TaskDetailsDropdownModal";
import { resizeImageToBase64 } from "../utils/commonMethods";
import CommonLoader from "../components/CommonLoader";
import CustomAlertModal from "../components/CustomAlertModal";
import { useAlert } from "../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import useResponsive from "../hooks/useResponsive";
import TimerProjectSelectionModal from "../components/Timer/TimerProjectSelectionModal";
import { useTimerContext } from "../contexts/TimerContext";

interface LocationState {
  projectData: any;
  materialData: any;
  tool: any;
  is_removal_task: boolean;
  isLastTaskToComplete: boolean;
}
interface DropDownItemType {
  label: string;
  value: string;
}
interface ErrorItemType {
  task_id: string;
  label: string;
}
const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({

  marginLeft: 0, // Remove left margin
  marginRight: 0, // Remove right margin to ensure symmetry
  marginTop: "25px",
  marginBottom: "25px",
  '.MuiFormControlLabel-label': {
    // fontFamily: "Nunito Sans500",
    fontFamily: 'Nunito Sans Regular500',
    fontSize: "16px",
    paddingRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  '.MuiCheckbox-root': {
    padding: 0,
    color: theme.palette.primary.main,
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  },
}));

const TaskDetailEditScreen: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [isLoading, setIsLoading] = useState(true);
  const [check, setCheck] = useState(false);
  const [materialStatus, setMaterialStatus] = useState<boolean>(false);
  const [displayImage, setDisplayImage] = useState<ImageArrayType[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [isPhotoForMultipleMaterial, setIsPhotoForMultipleMaterial] =
    useState(false);
  // Modal Handling
  const [materialImages, setMaterialImages] = useState<ImageType[]>([]);
  const [base64ImageFiles, setBase64ImageFiles] = useState<ImageType[]>([]);
  const [base64ImageFileObject, setBase64ImageFileObject] = useState({});
  const [medwerkerDateIssueAlert, setMedwerkerDateIssueAlert] = useState<
    ErrorItemType[]
  >([]);
  const [projectDateIssueAlert, setProjectDateIssueAlert] = useState<
    ErrorItemType[]
  >([]);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [dropdownItems, setDropDownItems] = useState<DropDownItemType[]>([]);
  const [dropDownTaskLists, setDropDownTaskLists] = useState<
    DropDownItemType[]
  >([]);

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { isDesktop, isMobile } = useResponsive()
  // const { projectData, materialData, tool, is_removal_task } = (location.state as LocationState) || {};
  const [taskData, setTaskData] = useState<LocationState | null>(null);

  // task details fetching API call
  const getTaskDetails = async () => {
    try {
      setIsLoading(true);
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        task_id: id,
      });
      apiService.setAuthToken(userToken);
      // Make an authenticated request
      const responseJson: AxiosResponse | null = await apiService.post(
        TaskDetailsApi,
        jsondata
      );
      if (
        responseJson &&
        responseJson.status &&
        responseJson.status_code === 1
      ) {
        if (!taskData?.is_removal_task) {
          const apiFiltered = responseJson.data.materials.filter(
            (data: any) => {
              return data.material_id === taskData?.materialData.material_id;
            }
          );
          setDisplayImage(apiFiltered[0].uploaded_images);
          setMaterialStatus(apiFiltered[0].material_status);
          setCheck(apiFiltered[0].material_status);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setDisplayImage(responseJson.data.materials[0].uploaded_images);
          setMaterialStatus(responseJson.data.materials[0].material_status);
          setCheck(responseJson.data.materials[0].material_status);
        }
      } else if (
        responseJson?.status === false &&
        (responseJson.status_code === 2 || responseJson.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        setIsLoading(false);
        navigate("/login", { replace: true });
      } else {
        setIsLoading(false);
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      setIsLoading(false);
    }
  };
  // material Listing in dropdown api call
  const getDropDownList = async () => {
    try {
      setIsLoading(true);
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        project_id: taskData?.projectData.project_id,
        task_id: taskData?.projectData.task_id,
        material_id: taskData?.materialData.material_id,
      });
      apiService.setAuthToken(userToken);
      // Make an authenticated request
      const responseJson: AxiosResponse | null = await apiService.post(
        MaterialWiseTaskListingDropDown,
        jsondata
      );
      if (
        responseJson &&
        responseJson.status &&
        responseJson.status_code === 1
      ) {
        // Map the data for DropDownPicker
        const dropdownItems = responseJson?.data.map((item: any) => ({
          label: item.label,
          value: item.id.toString(),
          // Convert id to string to ensure uniqueness and compatibility
        }));
        const additionalItem = {
          // Create a label using projectData (task, fauna, location)
          label: `${taskData?.projectData?.task} - ${taskData?.materialData?.material_name} - ${taskData?.projectData?.address} - ${taskData?.projectData?.fauna_type} `,
          value: taskData?.projectData?.task_id?.toString(),
        };
        setDropDownItems(dropdownItems);
        setDropDownTaskLists([...dropdownItems, additionalItem]);
      } else if (
        responseJson?.status === false &&
        (responseJson.status_code === 2 || responseJson.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        setIsLoading(false);
        navigate("/login", { replace: true });
      } else {
        setIsLoading(false);
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const deleteImage = async (activeSlide: number) => {
    let array = [...displayImage];
    let delId = array.splice(activeSlide, 1);
    let removedId = await delId[0].photo_id;
    materialPhotoUpdate(removedId.toString(), []);
  };

  const addImage = async (image: File) => {
    if (!taskData?.is_removal_task && isPhotoForMultipleMaterial) {
      await covertImage(image);
      setModalVisible(true);
    } else {
      setIsLoading(true);
      covertImage(image);
    }
  };



  const covertImage = async (image: File) => {
    try {
      // const blobSizeInMB = (image?.size / (1024 * 1024)).toFixed(2);
      // console.log(`Blob size: ${blobSizeInMB} MB`);
      let base64File = await resizeImageToBase64(image);
      base64File = base64File.split(",")[1];
      let imagePathName = image.name.replace(/^.*[\\\/]/, "");
      let imageWithoutExtension = imagePathName
        .split(".")
        .slice(0, -1)
        .join(".");
      setMaterialImages([
        ...materialImages,
        { filename: imageWithoutExtension, base64File: base64File },
      ]);
      const base64ImageFile = [
        { filename: imageWithoutExtension, base64File: base64File },
      ];
      const base64ImageFileObject = {
        filename: imageWithoutExtension,
        base64File: base64File,
      };
      setBase64ImageFiles(base64ImageFile);
      setBase64ImageFileObject(base64ImageFileObject);
      if (
        (!taskData?.is_removal_task && !isPhotoForMultipleMaterial) ||
        taskData?.is_removal_task
      ) {
        const delId = null;
        materialPhotoUpdate(delId, base64ImageFile);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const photoUploadMultipleTask = (
    selectedItems: string[],
    isCompleteMaterialStatus: boolean
  ) => {
    if (selectedItems.length > 0) {
      updatePhotoForSameProjectMaterial(
        isCompleteMaterialStatus,
        selectedItems
      );
    } else {
      // Show an alert when no items are selected
      showAlert(
        "Selecteer ten minste één taak uit de lijst om door te gaan.",
        "info"
      );
    }
  };

  // Function to filter tasks based on statusCode3TaskIds and return the label
  const getMedewerkerDateIssueTasks = (statusCodeTaskIds: string[]) => {
    return dropDownTaskLists
      .filter((task) => statusCodeTaskIds.includes(task.value.toString()))
      .map((task) => ({
        task_id: task.value.toString(),
        label: task.label,
      }));
  };

  // Function to filter tasks based on statusCode4TaskIds and return the label
  const getProjectDateIssueTasks = (statusCodeTaskIds: string[]) => {
    return dropDownTaskLists
      .filter((task) => statusCodeTaskIds.includes(task.value.toString()))
      .map((task) => ({
        task_id: task.value.toString(),
        label: task.label,
      }));
  };

  const handleNavigationAfterMaterialComplete = () => {
    if (taskData?.isLastTaskToComplete) {
      navigate("/task");
    } else {
      navigate(-1);
    }
    showAlert(t("taskDetailsUpdate.task_completed_successfully"), "success");
  };

  // updatePhotoForSameProjectMaterial details update API call
  const updatePhotoForSameProjectMaterial = async (
    isCompleteMaterialStatus: boolean,
    selectedItems: string[]
  ) => {
    setIsLoading(true);
    try {
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
      const selectedTaskIds = [
        ...selectedItems,
        taskData?.projectData.task_id ?? [],
      ];
      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        task_ids: selectedTaskIds,
        material_id: taskData?.materialData.material_id,
        photo: base64ImageFileObject,
        uitgovoerd: isCompleteMaterialStatus,
        project_id: taskData?.projectData.project_id,
      });

      apiService.setAuthToken(userToken);
      // Make an authenticated request
      const responseJson: AxiosResponse | null = await apiService.post(
        PhotoUpdateForSameProjectTaskMaterial,
        jsondata
      );

      if (responseJson?.status && responseJson?.status_code === 1) {
        if (
          responseJson.errors &&
          Array.isArray(responseJson.errors) &&
          responseJson.errors.length > 0
        ) {
          // Loop through each error in the errors array
          var statusCode3TaskIds: any[] = [];
          var statusCode4TaskIds: any[] = [];

          responseJson.errors.forEach((error: any) => {
            if (error.status_code === 3 || error.status_code === 4) {
              if (error.status_code === 3) {
                statusCode3TaskIds.push(error.task_id.toString());
              }
              if (error.status_code === 4) {
                statusCode4TaskIds.push(error.task_id.toString());
              }
            }
          });

          // Use the new functions to get the task lists
          const medeworkerDateIssueTasks =
            getMedewerkerDateIssueTasks(statusCode3TaskIds);
          setMedwerkerDateIssueAlert(medeworkerDateIssueTasks);

          const projectDateIssueTasks =
            getProjectDateIssueTasks(statusCode4TaskIds);
          setProjectDateIssueAlert(projectDateIssueTasks);

          setAlertModalVisible(true);
          setIsLoading(false);
        } else {
          // No errors, display success alert
          setModalVisible(false);
          if (isCompleteMaterialStatus) {
            handleNavigationAfterMaterialComplete();
          } else {
            showAlert(
              t("taskDetailsUpdate.photo_successfully_uploaded"),
              "success"
            );
            getTaskDetails();
          }
        }
      } else if (
        responseJson?.status === false &&
        (responseJson.status_code === 2 || responseJson.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        setIsLoading(false);
        navigate("/login", { replace: true });
      } else {
        setIsLoading(false);
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const materialPhotoUpdate = async (
    delid: string | null,
    base64ImageFiles: ImageType[]
  ) => {
    // photo upload api call
    // params delid, base64ImageFile
    setModalVisible(false);
    setIsLoading(true);
    try {
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        task_id: taskData?.projectData.task_id,
        material_id:
          !taskData?.is_removal_task
            ? taskData?.materialData.material_id
            : "",
        photos: base64ImageFiles,
        removedPhotoIdArray: [delid],
      });

      apiService.setAuthToken(userToken);
      // Make an authenticated request
      const responseJson: AxiosResponse | null = await apiService.post(
        MaterialUpdate,
        jsondata
      );

      if (responseJson?.status && responseJson?.status_code === 1) {
        // setDisplayImage([])
        if (delid === null)
          showAlert(
            t("taskDetailsUpdate.photo_successfully_uploaded"),
            "success"
          );
        else {
          showAlert(
            t("taskDetailsUpdate.photo_successfully_deleted"),
            "success"
          );
        }
        getTaskDetails();
        // setIsLoading(false)
      } else if (
        responseJson?.status === false &&
        (responseJson.status_code === 2 || responseJson.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        setIsLoading(false);
        navigate("/login", { replace: true });
      } else {
        setIsLoading(false);
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleCloseCustomAlert = () => {
    setMedwerkerDateIssueAlert([]);
    setProjectDateIssueAlert([]);
    setAlertModalVisible(false);
  };

  // material check box submit function
  const saveMaterialStatus = async (isCheckSubmit: boolean) => {
    const currentDate = new Date();
    const taskStartDate = new Date(taskData?.projectData?.task_start_date);
    if (currentDate >= taskStartDate) {
      materialComplete();
    } else {
      setCheck(false);
      showAlert(t("taskDetailsUpdate.taskStartDateValidation"), "info");
    }
  };
  // material check api call
  const materialComplete = async () => {
    setIsLoading(true);
    try {
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        task_id: taskData?.projectData.task_id,
        material_id:
          !taskData?.is_removal_task
            ? taskData?.materialData.material_id
            : "",
        is_removal_task: taskData?.is_removal_task,
        project_end_date: taskData?.projectData?.project_end_date,
        project_id: taskData?.projectData?.project_id,
        is_repair_non_material_task: taskData?.projectData?.is_repair_non_material_task
      });
      console.log("Material complete param", jsondata)
      apiService.setAuthToken(userToken);
      // Make an authenticated request
      const responseJson: AxiosResponse | null = await apiService.post(
        MaterialCheck,
        jsondata
      );
      if (responseJson?.status && responseJson?.status_code === 1) {
        setIsLoading(false);
        handleNavigationAfterMaterialComplete();
      } else if (
        responseJson?.status === false &&
        (responseJson.status_code === 2 || responseJson.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        setIsLoading(false);
        navigate("/login", { replace: true });
      } else if (
        responseJson?.status === false &&
        responseJson?.status_code === 3
      ) {
        setCheck(false);
        showAlert(t("taskDetailsUpdate.taskStartDateValidation"), "error");
      } else if (
        responseJson?.status === false &&
        responseJson?.status_code === 4
      ) {
        setCheck(false);
        showAlert(
          t(
            "taskDetailsUpdate.current_date_should_be_within_project_end_date_while_material_completion"
          ),
          "error"
        );
      } else {
        setIsLoading(false);
        setCheck(false);
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Check if data is available in the location state
        const stateData = (location.state as LocationState) || {};
        if (stateData.projectData && stateData.materialData && stateData.tool) {
          // If data is available in location state, use it and save to localStorage
          setTaskData({
            projectData: stateData.projectData,
            materialData: stateData.materialData,
            tool: stateData.tool,
            is_removal_task: stateData.is_removal_task,
            isLastTaskToComplete: stateData.isLastTaskToComplete,
          });
          LocalStorageUtil.setItem(`taskData_${id}`, stateData);
        } else {
          // If not available in location state, try to retrieve from localStorage
          const storedData = LocalStorageUtil.getItem(`taskData_${id}`, "");
          if (storedData) {
            setTaskData(storedData);
          }
        }
      } catch (err) {
        //console.error("Error fetching task data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [location, id]);

  useEffect(() => {
    if (taskData) {
      getTaskDetails();
      getDropDownList();
    }
  }, [taskData]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mb: 5, mt: "3px" }}>
      <Box sx={{ px: 3, flexGrow: 1, overflow: "auto", }}>
        <CommonLoader isVisible={isLoading} />
        <Typography
          gutterBottom
          fontFamily={"Nunito Sans Regular700"}
          sx={{
            color: theme.palette.blueColors.main,
            fontSize: "22px",
            my: "15px"
          }}
        >
          {/* {"Taak" + " "} */}
          {"Waarneming" + " "}
          {taskData?.projectData.task}
        </Typography>
        {taskData && (
          <Box
            sx={{
              display: "flex",
              flexDirection: !isDesktop ? "column" : "row",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <TaskEditHeader
                projectName={taskData.projectData.project_name}
                tool={taskData?.tool}
                setIsLoading={setIsLoading}
              />
              <TaskEditMaterialContainer
                is_removal_task={taskData.is_removal_task ?? 0}
                materialData={taskData.materialData}
                projectData={taskData.projectData}
                displayImage={displayImage}
              />
              <StyledFormControlLabel
                label="Gebruik foto voor meerdere taken"
                labelPlacement="start"
                value="start"
                disabled={materialStatus}
                control={
                  <Checkbox
                    checked={isPhotoForMultipleMaterial}
                    onChange={(e) => setIsPhotoForMultipleMaterial(e.target.checked)}
                  />
                }
              />
              {displayImage.length > 0 && isDesktop && (
                <TaskEditBottomContainer
                  isCheck={check}
                  setIsCheck={setCheck}
                  onSaveMaterialStatus={saveMaterialStatus}
                  materialStatus={materialStatus}
                />
              )}
            </Box>
            <CameraView
              images={displayImage}
              addImage={addImage}
              deleteImage={deleteImage}
              material_status={materialStatus}
              isLoading={isLoading}
            />
            {displayImage.length > 0 && !isDesktop && (
              <TaskEditBottomContainer
                isCheck={check}
                setIsCheck={setCheck}
                onSaveMaterialStatus={saveMaterialStatus}
                materialStatus={materialStatus}
              />
            )}
          </Box>
        )}
        <TaskDetailsDropdownModal
          modalVisible={modalVisible}
          handleModalClose={handleModalClose}
          dropdownItems={dropdownItems}
          photoUploadMultipleTask={photoUploadMultipleTask}
        />
      </Box>
      {alertModalVisible && (
        <CustomAlertModal
          medwerkerDateIssueAlert={medwerkerDateIssueAlert}
          projectDateIssueAlert={projectDateIssueAlert}
          handleClose={handleCloseCustomAlert}
        />
      )}
    </Box>
  );
};

export default TaskDetailEditScreen;
